// services/Notification.js
import axios from "axios";
import { getToken } from "../Utils/Common";
import token_valid from "./TokenValid";

// --- helper to normalize page content coming from UserNotification ---
const normalizeUserNotification = (item) => {
  // item is a UserNotification record
  const n = item?.notification || {};
  return {
    // ids
    userNotificationId: item?.userNotificationId ?? null,
    notificationId: n?.id ?? null,

    // notification core
    type: n?.type ?? null,
    message: n?.message ?? "",
    createdAt: n?.createdAt ?? null,
    referenceId: n?.referenceId ?? null,
    referenceEntity: n?.referenceEntity ?? null,

    // per-user state
    read: !!item?.read,
    deleted: !!item?.deleted,
    deliveredAt: item?.deliveredAt ?? null,
    readAt: item?.readAt ?? null,
  };
};

const normalizePage = (resp) => {
  if (!resp || typeof resp !== "object") return null;
  const content = Array.isArray(resp.content) ? resp.content : [];
  return {
    ...resp,
    content: content.map(normalizeUserNotification),
  };
};

// get unread notification count (unchanged path – backend now applies per-user)
const getUnreadNotificationCount = async () => {
  const ok = await token_valid();
  if (!ok) return "Something went wrong...";
  const token = getToken();
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/notification/unread/count`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      }
    );
    return data;
  } catch (e) {
    console.log("get unread notifications ", e);
    return "Something went wrong...";
  }
};

// get notification data with pagination (now returns UserNotification page)
const getNotificationData = async (offset, pageSize, filter) => {
  const ok = await token_valid();
  if (!ok) return "Something went wrong...";
  const token = getToken();
  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/notification/all/${offset}/${pageSize}`,
      {
        type: filter?.type ? filter.type.trim() : null, // enum or null
        status: filter?.status ? filter.status.trim() : null, // "READ"/"UNREAD"/null
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      }
    );
    // normalize to a flat object the UI can render easily
    let reply = normalizePage(data);
    console.log("Notification data reply:", reply);
    return data;
  } catch (e) {
    console.log("get notification ", e);
    return "Something went wrong...";
  }
};

// get notification data with pagination (now returns UserNotification page)
const getUserNotificationData = async (offset, pageSize, filter) => {
  const ok = await token_valid();
  if (!ok) return "Something went wrong...";
  const token = getToken();
  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/notification/user/all/${offset}/${pageSize}`,
      {
        type: filter?.type ? filter.type.trim() : null, // enum or null
        status: filter?.status ? filter.status.trim() : null, // "READ"/"UNREAD"/null
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      }
    );
    // normalize to a flat object the UI can render easily
    let reply = normalizePage(data);
    console.log("Notification data reply:", reply);
    return reply;
  } catch (e) {
    console.log("get notification ", e);
    return "Something went wrong...";
  }
};

// get notification types (backend may already filter based on roles)
const getNotificationTypes = async () => {
  const ok = await token_valid();
  if (!ok) return "Something went wrong...";
  const token = getToken();
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/notification/types`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      }
    );
    return data; // e.g. ["LOW_STOCK", "PENDING_INVOICE", ...]
  } catch (e) {
    console.log("get notification type ", e);
    return "Something went wrong...";
  }
};

// delete (IMPORTANT: use userNotificationId and user-scoped endpoint)
const deleteNotificationById = async (userNotificationId) => {
  const ok = await token_valid();
  if (!ok) return false;
  const token = getToken();
  try {
    // If you kept old paths, change this to your new per-user endpoint:
    // e.g. /api/notification/user/delete/{userNotificationId}
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/notification/user/delete/${userNotificationId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      }
    );
    return true;
  } catch (e) {
    console.log("Delete notification ", e);
    return false;
  }
};

// mark read/unread (IMPORTANT: use userNotificationId and user-scoped endpoint)
const markNotificationById = async (userNotificationId, mark) => {
  const ok = await token_valid();
  if (!ok) return false;
  const token = getToken();
  try {
    // If you kept old paths, change this to your new per-user endpoint:
    // e.g. /api/notification/user/mark/{userNotificationId}/{mark}
    await axios.put(
      `${process.env.REACT_APP_API_URL}/api/notification/user/mark/${userNotificationId}/${mark}`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      }
    );
    return true;
  } catch (e) {
    console.log("Mark Notification ", e);
    return false;
  }
};

export {
  getUnreadNotificationCount,
  getNotificationData,
  deleteNotificationById,
  markNotificationById,
  getNotificationTypes,
  getUserNotificationData,
};
